import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { ArrowLeft, CheckCircle, XCircle, WarningCircle, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * /admin/stripe-issuing-preflight
 *
 * One-screen cockpit showing every blocker between you and submitting the
 * Stripe Issuing application — or flipping ISSUANCE_PROVIDER=stripe to
 * make all newly-issued cards real Visa virtual cards.
 *
 * Each item is independently checkable so the UI can render green/yellow/red.
 */

function StatusPill({ status }) {
  const cfg = {
    done: { bg: "bg-emerald-100", fg: "text-emerald-700", border: "border-emerald-300", Icon: CheckCircle, label: "Done" },
    partial: { bg: "bg-amber-100", fg: "text-amber-800", border: "border-amber-300", Icon: WarningCircle, label: "Partial" },
    blocked: { bg: "bg-rose-100", fg: "text-rose-700", border: "border-rose-300", Icon: XCircle, label: "Blocked" },
  }[status] || { bg: "bg-stone-100", fg: "text-stone-700", border: "border-stone-300", Icon: WarningCircle, label: status };
  const { bg, fg, border, Icon, label } = cfg;
  return (
    <span className={`inline-flex items-center gap-1.5 ${bg} ${fg} border ${border} rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide`}>
      <Icon size={13} weight="fill" /> {label.toUpperCase()}
    </span>
  );
}

export default function StripeIssuingPreflight() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/stripe-issuing/preflight");
      setData(r.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pctDone = data ? Math.round((data.done / data.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/admin" className="inline-flex items-center gap-1 text-[#4A5C50] hover:text-[#112318] text-sm mb-6">
          <ArrowLeft size={16} /> Back to admin
        </Link>
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <h1 className="serif text-4xl text-[#112318]">Stripe Issuing — Pre-flight</h1>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs text-[#184A30] underline disabled:opacity-50"
            data-testid="refresh-preflight-btn"
          >
            {loading ? "Refreshing…" : "Refresh checks"}
          </button>
        </div>
        <p className="text-[#4A5C50] mt-2 leading-relaxed">
          Every requirement between "wallet cards today" and "real Visa cards via Stripe Issuing".
          Green items are done; rose items are blocking you from going live.
        </p>

        {data && (
          <div className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid="preflight-summary">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <p className="serif text-3xl text-[#112318]">{data.done} / {data.total}</p>
                <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] mt-1">
                  {data.overall === "ready" ? "All systems go" : `${data.blocked} blocker${data.blocked === 1 ? "" : "s"} remaining`}
                </p>
              </div>
              <StatusPill status={data.overall === "ready" ? "done" : "blocked"} />
            </div>
            <div className="mt-4 h-2 rounded-full bg-stone-100 overflow-hidden">
              <div className={`h-full ${data.overall === "ready" ? "bg-emerald-500" : "bg-[#184A30]"} transition-all`}
                style={{ width: `${pctDone}%` }} />
            </div>
          </div>
        )}

        {/* LLC formation walkthrough callout — surfaces the highest-impact unblocker first */}
        {data && data.overall !== "ready" && (
          <Link
            to="/admin/llc-formation-guide"
            className="mt-4 block rounded-2xl border border-[#184A30]/30 bg-gradient-to-r from-[#184A30]/8 to-emerald-50 p-5 hover:border-[#184A30]/60 transition-colors"
            data-testid="llc-guide-callout"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="text-[#184A30] font-semibold flex items-center gap-2">
                  Don't have a US LLC + EIN yet? <span className="text-[10px] tracking-[0.2em] uppercase bg-[#184A30] text-white rounded-full px-2 py-0.5">Start here</span>
                </p>
                <p className="text-xs text-[#4A5C50] mt-1 leading-relaxed">
                  Open the guided walkthrough — pick a formation provider (Stripe Atlas, Northwest, LegalZoom, DIY),
                  check off each step, and track LLC filing + EIN + registered agent + BOI + business bank account
                  + Stripe application in one place.
                </p>
              </div>
              <ArrowSquareOut size={18} className="text-[#184A30]" />
            </div>
          </Link>
        )}

        {error && (
          <div className="mt-6 rounded-xl border border-rose-300 bg-rose-50 p-4 text-rose-800 text-sm">
            {error}
          </div>
        )}

        <ul className="mt-6 space-y-3" data-testid="preflight-items">
          {(data?.items || []).map((item) => (
            <li
              key={item.key}
              className="rounded-2xl border border-[#E5E0D8] bg-white p-5"
              data-testid={`preflight-item-${item.key}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#112318]">{item.label}</p>
                  <p className="text-xs text-[#4A5C50] mt-1">{item.detail}</p>
                </div>
                <StatusPill status={item.status} />
              </div>
              {item.fix_url && item.status !== "done" && (
                <Link
                  to={item.fix_url}
                  className="inline-flex items-center gap-1 text-[#184A30] text-sm font-semibold mt-3"
                  data-testid={`fix-link-${item.key}`}
                >
                  Fix this <ArrowSquareOut size={14} />
                </Link>
              )}
              {item.key === "business_identity" && item.status !== "done" && (
                <Link
                  to="/admin/llc-formation-guide"
                  className="inline-flex items-center gap-1 text-[#184A30] text-sm font-semibold mt-3 ml-4 border-l border-[#E5E0D8] pl-4"
                  data-testid="business-identity-llc-link"
                >
                  Don't have an LLC yet? Open the formation guide <ArrowSquareOut size={14} />
                </Link>
              )}
            </li>
          ))}
        </ul>

        <div className="mt-8 rounded-2xl border border-[#184A30]/30 bg-[#184A30]/5 p-5 text-sm">
          <p className="text-[#184A30] font-semibold mb-2">After every check is green</p>
          <p className="text-[#4A5C50] leading-relaxed">
            Set <span className="mono bg-white px-1.5 py-0.5 rounded">ISSUANCE_PROVIDER=stripe</span> in your
            Emergent Deploy Panel env vars. Every card minted after that point becomes a real
            Visa virtual card accepted at any Visa-accepting merchant worldwide.
            Existing wallet cards stay closed-loop unless you bulk-migrate them via{" "}
            <Link to="/admin/stripe-issuing-guide" className="text-[#184A30] underline">the full guide</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
